/**
 * PipelineMapper.tsx - Multi-Dataset Pipeline Mapper
 * 
 * CHANGE HISTORY:
 * - 251128_1147 danielbeer: Created multi-dataset pipeline mapper with Panelr
 * 
 * Features:
 * - Multiple datasets in left-to-right pipeline
 * - Resizable Panelr panels for each dataset
 * - 1:1 field mapping with arrow indicators
 * - Tab/Shift-Tab keyboard navigation between datasets
 * - Global T/S toggles for field types and sample data
 * - Add/remove datasets dynamically
 * - Value propagation through pipeline
 * - Transform fields (regex/FM calc)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { DataSet, MapperPipeline } from '../types/index';
import { DataSetColumn } from './DataSetColumn';
import { exportPipelineToJSON, importPipelineFromJSON, validatePipelineJSON } from '../utils/pipelineJSON';
import { usePipelinePersistence } from '../hooks/usePipelinePersistence';
import { fileMakerIntegration } from '../utils/filemakerIntegration';
import './PipelineMapper.css';

export interface PipelineMapperProps {
  /** Initial pipeline configuration */
  pipeline?: MapperPipeline;
  
  /** Callback when pipeline changes */
  onChange?: (pipeline: MapperPipeline) => void;
  
  /** Theme */
  theme?: 'light' | 'dark';
  
  /** Enable persistence with Cachr */
  enablePersistence?: boolean;
  
  /** Pipeline ID for persistence */
  pipelineId?: string;
  
  /** Enable FileMaker integration */
  enableFileMakerIntegration?: boolean;
}

/**
 * PipelineMapper - Multi-dataset field mapping with transformations
 */
export const PipelineMapper: React.FC<PipelineMapperProps> = ({
  pipeline: initialPipeline,
  onChange,
  theme = 'light',
  enablePersistence = false,
  pipelineId = 'default-pipeline',
  enableFileMakerIntegration = false
}) => {
  // Use persistence hook if enabled
  const defaultPipeline: MapperPipeline = initialPipeline || {
    datasets: [{
      id: 'dataset-0',
      name: 'Source',
      fields: [],
      data: [],
      position: 0,
      mappings: [],
      width: 300,
      enabled: true
    }],
    showFieldTypes: true,
    showSampleData: true,
    compact: false
  };

  const persistence = usePipelinePersistence(defaultPipeline, {
    pipelineId,
    enableVersionTracking: enablePersistence,
    onChange
  });

  // Use persistence or local state
  const [localPipeline, setLocalPipeline] = useState<MapperPipeline>(defaultPipeline);
  const pipeline = enablePersistence ? persistence.pipeline : localPipeline;
  const setPipeline = enablePersistence ? persistence.setPipeline : (p: MapperPipeline | ((prev: MapperPipeline) => MapperPipeline)) => {
    const newPipeline = typeof p === 'function' ? p(localPipeline) : p;
    setLocalPipeline(newPipeline);
    if (onChange) onChange(newPipeline);
  };

  const [activeDatasetId, setActiveDatasetId] = useState<string | undefined>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Setup FileMaker integration
  useEffect(() => {
    if (enableFileMakerIntegration) {
      fileMakerIntegration.setPipeline(pipeline);
      fileMakerIntegration.setUpdateCallback((updated) => {
        setPipeline(updated);
      });
    }
  }, [enableFileMakerIntegration, pipeline, setPipeline]);

  // Add dataset to pipeline
  const handleAddDataset = useCallback(() => {
    const newPosition = pipeline.datasets.length;
    const newDataset: DataSet = {
      id: `dataset-${newPosition}`,
      name: `Dataset ${newPosition + 1}`,
      fields: [],
      position: newPosition,
      mappings: [],
      width: 300
    };

    const newPipeline = {
      ...pipeline,
      datasets: [...pipeline.datasets, newDataset]
    };

    setPipeline(newPipeline);
  }, [pipeline, setPipeline]);

  // Remove dataset from pipeline
  const handleRemoveDataset = useCallback((datasetId: string) => {
    const newDatasets = pipeline.datasets
      .filter(ds => ds.id !== datasetId)
      .map((ds, index) => ({ ...ds, position: index }));

    const newPipeline = {
      ...pipeline,
      datasets: newDatasets
    };

    setPipeline(newPipeline);
  }, [pipeline, setPipeline]);

  // Update dataset
  const handleDatasetChange = useCallback((datasetId: string, updates: Partial<DataSet>) => {
    const newDatasets = pipeline.datasets.map(ds =>
      ds.id === datasetId ? { ...ds, ...updates } : ds
    );

    const newPipeline = {
      ...pipeline,
      datasets: newDatasets
    };

    setPipeline(newPipeline);
  }, [pipeline, setPipeline]);

  // Toggle field types visibility
  const handleToggleFieldTypes = useCallback(() => {
    setPipeline((prev: MapperPipeline) => ({
      ...prev,
      showFieldTypes: !prev.showFieldTypes
    }));
  }, [setPipeline]);

  // Toggle sample data visibility
  const handleToggleSampleData = useCallback(() => {
    setPipeline((prev: MapperPipeline) => ({
      ...prev,
      showSampleData: !prev.showSampleData
    }));
  }, [setPipeline]);

  // Handle dataset reordering
  const handleDatasetReorder = useCallback((datasetId: string, newPosition: number) => {
    const oldPosition = pipeline.datasets.findIndex(ds => ds.id === datasetId);
    if (oldPosition === -1 || oldPosition === newPosition) return;

    const newDatasets = [...pipeline.datasets];
    const [movedDataset] = newDatasets.splice(oldPosition, 1);
    newDatasets.splice(newPosition, 0, movedDataset);

    // Update positions
    const updatedDatasets = newDatasets.map((ds, index) => ({
      ...ds,
      position: index
    }));

    setPipeline({
      ...pipeline,
      datasets: updatedDatasets
    });
  }, [pipeline, setPipeline]);

  // Export pipeline to JSON
  const handleExportJSON = useCallback(() => {
    const json = exportPipelineToJSON(pipeline);
    const jsonString = JSON.stringify(json, null, 2);
    
    // Create download link
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [pipeline]);

  // Import pipeline from JSON
  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);
          const validation = validatePipelineJSON(json);
          
          if (!validation.valid) {
            alert(`Invalid pipeline JSON:\n${validation.errors.join('\n')}`);
            return;
          }
          
          const importedPipeline = importPipelineFromJSON(json);
          setPipeline(importedPipeline);
          
          if (onChange) {
            onChange(importedPipeline);
          }
        } catch (error) {
          alert(`Error parsing JSON: ${error}`);
        }
      };
      reader.readAsText(file);
    };
    
    input.click();
  }, [onChange]);

  // Save as template (using Templatr if available)
  const handleSaveAsTemplate = useCallback(async () => {
    const templateName = prompt('Enter template name:');
    if (!templateName) return;

    try {
      // Try to use Templatr if available
      if (typeof window !== 'undefined' && (window as any).Templatr) {
        const Templatr = (window as any).Templatr;
        await Templatr.save({
          id: `mappr-template-${Date.now()}`,
          name: templateName,
          type: 'pipeline-mapping',
          data: pipeline,
          metadata: {
            datasetCount: pipeline.datasets.length,
            fieldCount: pipeline.datasets.reduce((sum, ds) => sum + ds.fields.length, 0),
            createdAt: new Date().toISOString()
          }
        });
        alert(`Template "${templateName}" saved successfully!`);
      } else {
        // Fallback: save to localStorage
        const templates = JSON.parse(localStorage.getItem('mappr-templates') || '[]');
        templates.push({
          id: `template-${Date.now()}`,
          name: templateName,
          pipeline,
          createdAt: new Date().toISOString()
        });
        localStorage.setItem('mappr-templates', JSON.stringify(templates));
        alert(`Template "${templateName}" saved to local storage!`);
      }
    } catch (error) {
      alert(`Error saving template: ${error}`);
    }
  }, [pipeline]);

  // Load template
  const handleLoadTemplate = useCallback(async () => {
    try {
      // Try to use Templatr if available
      if (typeof window !== 'undefined' && (window as any).Templatr) {
        const Templatr = (window as any).Templatr;
        const templates = await Templatr.list({ type: 'pipeline-mapping' });
        
        if (templates.length === 0) {
          alert('No templates found');
          return;
        }
        
        // Simple selection (in production, use a proper dialog)
        const templateNames = templates.map((t: any) => `${t.name} (${t.metadata?.datasetCount || 0} datasets)`).join('\n');
        const selection = prompt(`Select template:\n${templateNames}\n\nEnter template name:`);
        
        if (selection) {
          const template = templates.find((t: any) => t.name === selection || t.name.startsWith(selection));
          if (template) {
            setPipeline(template.data);
            alert(`Template "${template.name}" loaded!`);
          }
        }
      } else {
        // Fallback: load from localStorage
        const templates = JSON.parse(localStorage.getItem('mappr-templates') || '[]');
        
        if (templates.length === 0) {
          alert('No templates found');
          return;
        }
        
        const templateNames = templates.map((t: any) => t.name).join('\n');
        const selection = prompt(`Select template:\n${templateNames}\n\nEnter template name:`);
        
        if (selection) {
          const template = templates.find((t: any) => t.name === selection);
          if (template) {
            setPipeline(template.pipeline);
            alert(`Template "${template.name}" loaded!`);
          }
        }
      }
    } catch (error) {
      alert(`Error loading template: ${error}`);
    }
  }, [setPipeline]);

  // Keyboard navigation: Tab/Shift-Tab between datasets
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        
        const currentIndex = pipeline.datasets.findIndex(ds => ds.id === activeDatasetId);
        
        if (e.shiftKey) {
          // Shift-Tab: Previous dataset
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : pipeline.datasets.length - 1;
          setActiveDatasetId(pipeline.datasets[prevIndex].id);
        } else {
          // Tab: Next dataset
          const nextIndex = currentIndex < pipeline.datasets.length - 1 ? currentIndex + 1 : 0;
          setActiveDatasetId(pipeline.datasets[nextIndex].id);
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => container.removeEventListener('keydown', handleKeyDown);
    }
  }, [pipeline.datasets, activeDatasetId]);

  return (
    <div 
      ref={containerRef}
      className={`pipeline-mapper pipeline-mapper--${theme}`}
      tabIndex={0}
    >
      {/* Global controls */}
      <div className="pipeline-mapper__toolbar">
        <div className="pipeline-mapper__title">
          <h2>Data Pipeline Mapper</h2>
          <span className="pipeline-mapper__count">{pipeline.datasets.length} datasets</span>
        </div>
        
        <div className="pipeline-mapper__controls">
          <button
            className="pipeline-mapper__toggle-btn"
            onClick={handleToggleFieldTypes}
            title="Toggle field types"
            data-active={pipeline.showFieldTypes}
          >
            T
          </button>
          <button
            className="pipeline-mapper__toggle-btn"
            onClick={handleToggleSampleData}
            title="Toggle sample data"
            data-active={pipeline.showSampleData}
          >
            S
          </button>
          <button
            className="pipeline-mapper__action-btn"
            onClick={handleExportJSON}
            title="Export pipeline as JSON"
          >
            📥 Export
          </button>
          <button
            className="pipeline-mapper__action-btn"
            onClick={handleImportJSON}
            title="Import pipeline from JSON"
          >
            📤 Import
          </button>
          <button
            className="pipeline-mapper__action-btn"
            onClick={handleSaveAsTemplate}
            title="Save as template"
          >
            💾 Save Template
          </button>
          <button
            className="pipeline-mapper__action-btn"
            onClick={handleLoadTemplate}
            title="Load template"
          >
            📋 Load Template
          </button>
          <button
            className="pipeline-mapper__add-btn"
            onClick={handleAddDataset}
            title="Add dataset"
          >
            + Add Dataset
          </button>
        </div>
      </div>

      {/* Pipeline datasets */}
      <div className="pipeline-mapper__pipeline">
        {pipeline.datasets.map((dataset, index) => (
          <React.Fragment key={dataset.id}>
            <DataSetColumn
              dataset={dataset}
              previousDataset={index > 0 ? pipeline.datasets[index - 1] : undefined}
              showFieldTypes={pipeline.showFieldTypes}
              showSampleData={pipeline.showSampleData}
              compact={pipeline.compact}
              isActive={dataset.id === activeDatasetId}
              onDatasetChange={(updates) => handleDatasetChange(dataset.id, updates)}
              onRemove={pipeline.datasets.length > 1 ? () => handleRemoveDataset(dataset.id) : undefined}
              onFieldFocus={() => {
                setActiveDatasetId(dataset.id);
              }}
              onReorder={handleDatasetReorder}
            />
            
            {/* Arrow connector between datasets */}
            {index < pipeline.datasets.length - 1 && (
              <div className="pipeline-mapper__connector">
                <div className="pipeline-mapper__arrow">→</div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
